import { CLIENT_CONTAINER, CLIENT_PLATFORM } from '@lowcode/mcp-protocol/src/base/common';
import { getAppVersion, getSystemType, SystemType } from '@nibfe/mkt-env-api';
const isMRN = false;
import { Platform, RecClient, RecHomestaySource, Source } from '../types/domain';
import { string2PositiveInt, stringToFloat } from './number';
import { getRiskRequest } from './risk';
// R007-fix(问题3)：3.18 推荐供给的「客户端核心参数」utm_*（对齐同仓 tex 组件 city-wish-free-card / team-ranking-free）
const UTM_CAMPAIGN = 'texactivity';
const UTM_SOURCE = 'meituaninternaltest';
const UTM_MEDIUM_IOS = 'iphone';
const UTM_MEDIUM_ANDROID = 'android';
/**
 * 根据 commonParams.platform（CLIENT_PLATFORM 枚举）派生业务请求的 Platform 枚举值
 */
export function getRequestPlatform(platform) {
    return platform === CLIENT_PLATFORM.DP ? Platform.DIANPING : Platform.MEITUAN;
}
/**
 * 根据 commonParams.clientContainer（CLIENT_CONTAINER 枚举）派生业务请求的 Source 枚举值
 */
export function getRequestSource(clientContainer) {
    switch (clientContainer) {
        case CLIENT_CONTAINER.DIANPING_APP:
        case CLIENT_CONTAINER.DIANPING_WEBVIEW:
            return Source.DIANPING_APP;
        case CLIENT_CONTAINER.MEITUAN_WECHAT_MINI_PROGRAM:
            return Source.WECHAT_MEITUAN_MINI;
        case CLIENT_CONTAINER.HOTEL_WECHAT_MINI_PROGRAM:
            return Source.WECHAT_HOTEL_MINI;
        case CLIENT_CONTAINER.DIANPING_WECHAT_MINI_PROGRAM:
            return Source.WECHAT_DIANPING_MINI;
        default:
            return Source.MEITUAN_APP;
    }
}
/**
 * 从 commonParams 中一次性提取 platform 和 source 枚举值
 * commonParams 可能为 undefined（页面初始化未就绪时），此时降级到默认值
 *
 * 注意：这里只产出「所有接口本就通用」的基座参数。3.18 推荐供给（rec）专属的客户端核心参数
 * （utm_* / client / source4Homestay / version_name）不放进基座，避免被各接口 `{ ...feCommonParams }`
 * 整体展开时误传给非 rec 接口；rec 专属参数由 getRecExtraParams 单独派生、仅在 rec 接口构造时注入。
 */
export async function getRequestBaseParams(commonParams) {
    const riskRequest = await getRiskRequest(commonParams);
    // version/uuid 优先取「设备级可靠源」（对齐同仓 tex 组件 city-wish-free-card / team-ranking-free）：
    // 版本用 getAppVersion()，避免依赖端注入的 commonParams.appVersion（部分端/预览为空）；commonParams / 风控参数兜底。
    const version = getAppVersion() || commonParams?.appVersion || riskRequest?.version || '';
    const uuid = commonParams?.uuid || riskRequest?.uuid || '';
    const feCommonParams = {
        userId: commonParams?.userId,
        token: commonParams?.token,
        locateCityId: string2PositiveInt(commonParams?.cityInfo?.locCityId),
        cityId: string2PositiveInt(commonParams?.cityInfo?.cityId),
        latitude: stringToFloat(commonParams?.lat),
        longitude: stringToFloat(commonParams?.lng),
        uuid,
        version,
        riskRequest,
        platform: getRequestPlatform(commonParams?.platform ?? CLIENT_PLATFORM.MT),
        source: getRequestSource(commonParams?.clientContainer ?? CLIENT_CONTAINER.MEITUAN_APP)
    };
    return feCommonParams;
}
/**
 * R007 3.18：仅推荐供给（rec）接口需要的「客户端核心参数」（后端先读 Header 再读 parameter）。
 * 刻意从基座 getRequestBaseParams 拆出，避免被其它接口整体展开时误传。
 * 同步派生（不依赖风控）：复用 base 的 uuid/version，端类型由 getSystemType 判定。
 */
export function getRecExtraParams(base, commonParams) {
    const systemType = getSystemType();
    return {
        utmCampaign: UTM_CAMPAIGN,
        utmContent: base.uuid,
        utmMedium: systemType === SystemType.ios ? UTM_MEDIUM_IOS : systemType === SystemType.android ? UTM_MEDIUM_ANDROID : '',
        utmSource: UTM_SOURCE,
        utmTerm: base.version,
        // 设备端（client）
        client: systemType === SystemType.ios ? RecClient.IPHONE : systemType === SystemType.android ? RecClient.ANDROID : RecClient.UNKNOWN,
        // 民宿来源端：在美团 app 内 → MT，其他 → UNKNOWN（端注入优先，MRN 兜底美团 app）
        source4Homestay: (commonParams?.clientContainer ?? (isMRN ? CLIENT_CONTAINER.MEITUAN_APP : CLIENT_CONTAINER.OTHER)) === CLIENT_CONTAINER.MEITUAN_APP ? RecHomestaySource.MT : RecHomestaySource.UNKNOWN
    };
}
